import React, { createContext, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

// Config
import {
  mediaItemUploadUrl,
  medicalRecordUploadUrl
} from "../../config/apiConfig";
import { useAppInsights } from "../../utils/telemetry/AppInsights";

// Actions
import { parseFetchOptions } from "../../state/user/actions";
import { retryAddMedia } from "../../state/media/actions";
import { retryAddMedia as retryAddMediaPerpetrators } from "../../state/mediaPerpetrators/actions";

// Components
import FileUpload from "../FileUpload/FileUpload";

const MediaReuploadContext = createContext();
export const useMediaReupload = () => useContext(MediaReuploadContext);

const uploadUrl = {
  mediaPage: mediaItemUploadUrl,
  medicalRecordsPage: medicalRecordUploadUrl,
  incidentPage: mediaItemUploadUrl
};

const MediaReuploadProvider = ({ children, page, caseId, noUpload }) => {
  const [elementId, setElementId] = useState(null);
  const { trackException } = useAppInsights();

  const dispatch = useDispatch();
  const media = useSelector(s => s.media);
  const mediaPerpetrators = useSelector(s => s.mediaPerpetrators);
  const medicalRecords = useSelector(s => s.medicalRecords);

  const isIncident = page === "incidentPage";

  const uploadStatus = {
    mediaPage: media,
    incidentPage: mediaPerpetrators,
    medicalRecordsPage: medicalRecords
  };

  const closeReupload = () => setElementId(null);
  const openReupload = id => setElementId(id);

  const handleSubmit = files => {
    if (!noUpload) {
      dispatch(
        parseFetchOptions(({ headers: { Authorization, ...headers } }) => {
          const url = uploadUrl[page](caseId, elementId);
          const options = {
            method: "PUT",
            headers: { ...headers, Authorization }
          };
          fetch(url, options)
            .then(res => res.json())
            .then(reuploadData => {
              const retryProps = {
                caseId,
                image: files[0].file,
                id: reuploadData.id,
                url: reuploadData.uploadURL,
                onFail: () =>
                  trackException(
                    `Failed to reupload media file ${reuploadData.id} at ${page} of case ${caseId}`
                  )
              };
              if (isIncident) dispatch(retryAddMediaPerpetrators(retryProps));
              else dispatch(retryAddMedia(retryProps));
            })
            .catch(() => {
              trackException(
                `Failed to reupload media ${elementId} at ${page} of ${caseId}`
              );
            });
        })
      );
    }
  };

  return (
    <MediaReuploadContext.Provider
      value={{
        openReupload,
        closeReupload
      }}
    >
      <>
        {children}
        {!noUpload && (
          <FileUpload
            onClose={closeReupload}
            onSubmit={handleSubmit}
            open={Boolean(elementId)}
            noDescriptions
            uploadErrors={uploadStatus[page].uploadErrors}
            uploadProgress={uploadStatus[page].uploadProgress}
          />
        )}
      </>
    </MediaReuploadContext.Provider>
  );
};

MediaReuploadProvider.propTypes = {
  children: PropTypes.node,
  page: PropTypes.string,
  caseId: PropTypes.string.isRequired,
  noUpload: PropTypes.bool
};

MediaReuploadProvider.defaultProps = {
  children: null,
  page: null,
  noUpload: false
};

export default MediaReuploadProvider;
